import { useEffect, useState } from "react";
import { Brain, Sparkles, Save, ShieldCheck, RotateCcw, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { formatDateTimeAU } from "@/lib/formatDateAU";
import { toast } from "sonner";

const STATUS_STY = {
  draft:    { bg: "bg-slate-100",   text: "text-slate-700",   label: "Draft" },
  edited:   { bg: "bg-amber-50",    text: "text-amber-700",   label: "Edited (not approved)" },
  approved: { bg: "bg-emerald-50",  text: "text-emerald-700", label: "Approved · Customer-visible" },
};

export function AiSummaryPanel({ testId, isPublished, isManager, onSummaryChange }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSend, setAutoSend] = useState(false);
  const [draft, setDraft] = useState({ narrative: "", key_findings: "", recommended_actions: "" });
  // One-click "force regenerate" of an already-approved summary. Two API
  // hops under the hood (unapprove → generate) but a single confirm dialog
  // for the admin so they don't have to remember the dance.
  const [confirmForceRegen, setConfirmForceRegen] = useState(false);

  // Wrap setSummary so we always bubble the latest doc up to the parent
  // (LabDetail) for the send-readiness chip. Null when the panel has no
  // summary (e.g. 404 on initial load).
  const applySummary = (s) => {
    setSummary(s);
    if (onSummaryChange) onSummaryChange(s);
  };

  const load = async () => {
    if (!testId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/ai-summaries/${testId}`);
      applySummary(data);
      setDraft({
        narrative: data.narrative || "",
        key_findings: (data.key_findings || []).join("\n"),
        recommended_actions: (data.recommended_actions || []).join("\n"),
      });
    } catch (e) {
      if (e.response?.status !== 404) toast.error(formatApiError(e.response?.data?.detail) || "Could not load summary");
      applySummary(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [testId]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/admin/ai-summaries/${testId}/generate`);
      applySummary(data);
      setDraft({
        narrative: data.narrative || "",
        key_findings: (data.key_findings || []).join("\n"),
        recommended_actions: (data.recommended_actions || []).join("\n"),
      });
      toast.success("AI draft generated · review before approving");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  // Force-regenerate an already-APPROVED summary in one click. Internally:
  //   1. unapprove the current approved doc (audit: "unapproved")
  //   2. generate a fresh draft (audit: "generated")
  // Both actions are recorded in the audit log so nothing is lost.
  const forceRegenerate = async () => {
    if (!summary) return;
    setGenerating(true);
    try {
      await api.post(`/admin/ai-summaries/${summary.id}/unapprove`);
      const { data } = await api.post(`/admin/ai-summaries/${testId}/generate`);
      applySummary(data);
      setDraft({
        narrative: data.narrative || "",
        key_findings: (data.key_findings || []).join("\n"),
        recommended_actions: (data.recommended_actions || []).join("\n"),
      });
      toast.success("Approved summary pulled back and re-drafted · review before re-approving");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Force-regenerate failed");
    } finally {
      setGenerating(false);
      setConfirmForceRegen(false);
    }
  };

  const saveEdits = async () => {
    if (!summary) return;
    setSaving(true);
    try {
      const payload = {
        narrative: draft.narrative,
        key_findings: draft.key_findings.split("\n").map((s) => s.trim()).filter(Boolean),
        recommended_actions: draft.recommended_actions.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.post(`/admin/ai-summaries/${summary.id}/edit`, payload);
      applySummary(data);
      toast.success("Edits saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    if (!summary) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/ai-summaries/${summary.id}/approve`, { auto_send: autoSend });
      applySummary(data);
      if (autoSend) toast.success("Approved · PDF generated and emailed to customer");
      else toast.success("Approved — customer will see this on their record");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Approval failed");
    } finally {
      setSaving(false);
    }
  };

  const unapprove = async () => {
    if (!summary) return;
    if (!confirm("Pull this approved summary back to draft? Customers will stop seeing it immediately.")) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/ai-summaries/${summary.id}/unapprove`);
      applySummary(data);
      toast.info("Summary moved back to edit state");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not unapprove");
    } finally {
      setSaving(false);
    }
  };

  // Panel is shown across the full review window (awaiting_review →
  // approved → published) so admins can generate / review / approve the
  // AI summary before clicking "Send to Customer". The outer JSX in
  // LabDetail.jsx guarantees we only mount here for those statuses, so no
  // pre-publish stub is needed.

  return (
    <section data-testid="ai-summary-panel" className="rounded-3xl bg-white border border-slate-200 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-cyan-600" />
          <h2 className="font-display text-lg font-semibold text-slate-900">AI summary (Claude Sonnet 4.6)</h2>
          {summary && (
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_STY[summary.status]?.bg} ${STATUS_STY[summary.status]?.text}`}>
              {STATUS_STY[summary.status]?.label || summary.status}
            </span>
          )}
        </div>
        <Button
          data-testid="ai-generate-btn"
          onClick={summary?.status === "approved" ? () => setConfirmForceRegen(true) : generate}
          disabled={generating}
          className="btn-aqua rounded-full font-semibold"
          title={summary?.status === "approved"
            ? "Pulls the approved summary back to draft and re-generates a fresh one in one step."
            : undefined}
        >
          {generating
            ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Generating…</>
            : <><Sparkles className="w-4 h-4 mr-1.5" />{summary
                ? (summary.status === "approved" ? "Force re-generate" : "Re-generate")
                : "Generate AI summary"}</>}
        </Button>
      </div>

      <p className="text-xs text-slate-500 mb-5 leading-relaxed bg-slate-50 rounded-xl p-3 inline-flex items-start gap-2">
        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        The deterministic recommendation engine remains the source of truth. Claude only <em>explains and polishes</em> — it cannot invent or override recommendations. <strong>Nothing is shown to the customer until you click Approve.</strong>
      </p>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : !summary ? (
        <div className="text-sm text-slate-500 italic">No AI summary yet — click <strong>Generate</strong> to draft one. The deterministic recommendations are already visible to the customer.</div>
      ) : (
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Customer-facing narrative</Label>
            <Textarea data-testid="ai-narrative" value={draft.narrative} onChange={(e) => setDraft({ ...draft, narrative: e.target.value })} disabled={summary.status === 'approved'} rows={6} className="mt-1.5 rounded-xl" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Key findings (one per line)</Label>
            <Textarea data-testid="ai-findings" value={draft.key_findings} onChange={(e) => setDraft({ ...draft, key_findings: e.target.value })} disabled={summary.status === 'approved'} rows={4} className="mt-1.5 rounded-xl font-mono text-xs" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Recommended actions (one per line)</Label>
            <Textarea data-testid="ai-actions" value={draft.recommended_actions} onChange={(e) => setDraft({ ...draft, recommended_actions: e.target.value })} disabled={summary.status === 'approved'} rows={4} className="mt-1.5 rounded-xl font-mono text-xs" />
          </div>

          {summary.what_this_means && Object.keys(summary.what_this_means).length > 0 && (
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">"What this means" explanations</Label>
              <div className="mt-1.5 space-y-2">
                {Object.entries(summary.what_this_means).map(([k, v]) => (
                  <div key={k} className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                    <code className="text-[10px] font-mono text-cyan-700 font-bold">{k}</code>
                    <div className="text-xs text-slate-700 mt-1">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-slate-100">
            <div className="text-[11px] text-slate-500">
              Generated {formatDateTimeAU(summary.generated_at)}
              {summary.approved_at && <> · Approved {formatDateTimeAU(summary.approved_at)} by <span className="font-mono">{summary.approved_by}</span></>}
            </div>
            <div className="flex gap-2">
              {summary.status !== "approved" ? (
                <>
                  <Button data-testid="ai-save-edit" variant="outline" onClick={saveEdits} disabled={saving} className="rounded-full font-semibold">
                    <Save className="w-4 h-4 mr-1.5" /> Save edits
                  </Button>
                  {isManager && (
                    <div className="flex items-center gap-3">
                      <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700 select-none" data-testid="ai-auto-send-label">
                        <input type="checkbox" data-testid="ai-auto-send" checked={autoSend} onChange={(e) => setAutoSend(e.target.checked)} className="w-4 h-4 rounded accent-emerald-600" />
                        Generate and email customer report after approval
                      </label>
                      <Button data-testid="ai-approve" onClick={approve} disabled={saving} className="rounded-full font-semibold bg-emerald-600 hover:bg-emerald-700">
                        <ShieldCheck className="w-4 h-4 mr-1.5" /> Approve{autoSend ? " & auto-send" : ""}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                isManager && (
                  <Button data-testid="ai-unapprove" variant="outline" onClick={unapprove} disabled={saving} className="rounded-full font-semibold text-rose-600 border-rose-200 hover:bg-rose-50">
                    <RotateCcw className="w-4 h-4 mr-1.5" /> Pull back to draft
                  </Button>
                )
              )}
            </div>
          </div>
        </div>
      )}
      {confirmForceRegen && (
        <div data-testid="ai-force-regen-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
            <h3 className="font-display text-lg font-semibold text-slate-900">Force re-generate approved summary?</h3>
            <div className="text-sm text-slate-600 mt-3 leading-relaxed space-y-2">
              <p>This will <strong>pull the approved summary back to draft</strong> and <strong>generate a fresh draft</strong> in one step.</p>
              <p>Customers will stop seeing the current approved summary on their record immediately. You&rsquo;ll need to review the new draft and click <em>Approve</em> again before it becomes customer-visible.</p>
              <p className="text-xs text-slate-500 italic">Both actions are recorded in the AI summary audit log.</p>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button
                data-testid="ai-force-regen-cancel"
                variant="ghost"
                onClick={() => setConfirmForceRegen(false)}
                disabled={generating}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                data-testid="ai-force-regen-confirm"
                onClick={forceRegenerate}
                disabled={generating}
                className="rounded-full font-semibold btn-aqua"
              >
                {generating
                  ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Working…</>
                  : <><Sparkles className="w-4 h-4 mr-1.5" />Yes, force re-generate</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
